import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getUserProfile } from '../../services/userService';
import {
  cancelSubscription,
  changeSubscription,
  createCheckout,
  resumeSubscription,
} from '../../services/paymentService';
import './SupportPage.css';

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing']);
const CHECKOUT_SUCCESS_MESSAGE =
  'Thank you for supporting ASiJoLab. Your contribution helps keep the project running and supports its continued development.';
const CHECKOUT_CANCEL_MESSAGE =
  'Checkout was canceled. No changes were made to your support selection.';
const CHECKOUT_SYNC_RETRY_DELAYS_MS = [0, 750, 1750];

const MONTHLY_PLANS = [
  {
    optionId: 'supporter',
    title: 'Supporter',
    price: '€5 / month',
    description: 'Voluntary monthly support.',
    details: [
      'Supports server costs and maintenance',
      'Helps keep ASiJoLab online for the community',
      'Cancel anytime.',
    ],
    checkoutLabel: 'Support with €5/month',
    currentLabel: 'Current monthly support',
  },
  {
    optionId: 'supporter_plus',
    title: 'Supporter Plus',
    price: '€10 / month',
    description: 'Voluntary monthly support.',
    details: [
      'Supporter Plus recognition on your profile',
      'Helps fund new features and improvements',
      'Supports continued development',
      'Cancel anytime.',
    ],
    checkoutLabel: 'Support with €10/month',
    currentLabel: 'Current monthly support',
    emphasized: true,
    label: 'More support',
  },
];

const ONE_TIME_OPTIONS = [
  {
    optionId: 'one_time_10',
    price: '€10',
    label: 'One-time voluntary support',
    description: 'A quick way to say thank you and support our work.',
    buttonLabel: 'Support with €10',
  },
  {
    optionId: 'one_time_25',
    price: '€25',
    label: 'One-time voluntary support',
    description: 'Extra support for the development of new features.',
    buttonLabel: 'Support with €25',
  },
  {
    optionId: 'one_time_50',
    price: '€50',
    label: 'One-time voluntary support',
    description: 'A stronger boost for ASiJoLab and its future.',
    buttonLabel: 'Support with €50',
  },
];

const MONTHLY_PLAN_LABELS = {
  supporter: 'Supporter',
  premium: 'Supporter Plus',
};
const SUBSCRIPTION_OPTION_TO_PLAN = {
  supporter: 'supporter',
  supporter_plus: 'premium',
};

const formatSupportDate = (value) => {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('en', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const getProfileWithSubscriptionState = (profile, subscriptionState) => {
  if (!profile || !subscriptionState) return profile;

  return {
    ...profile,
    stripeSubscriptionPlan:
      SUBSCRIPTION_OPTION_TO_PLAN[subscriptionState.optionId] ??
      profile.stripeSubscriptionPlan,
    stripeSubscriptionStatus:
      subscriptionState.status ?? profile.stripeSubscriptionStatus,
    stripeCancelAtPeriodEnd:
      subscriptionState.cancelAtPeriodEnd ?? profile.stripeCancelAtPeriodEnd,
    stripeCurrentPeriodEnd:
      subscriptionState.currentPeriodEnd ?? profile.stripeCurrentPeriodEnd,
  };
};

const isActiveRecurring = (profile) =>
  MONTHLY_PLAN_LABELS[profile?.stripeSubscriptionPlan] &&
  ACTIVE_SUBSCRIPTION_STATUSES.has(profile?.stripeSubscriptionStatus);

const getMonthlyAction = ({ plan, profile, scheduledChange }) => {
  const hasRecurring = isActiveRecurring(profile);
  const currentPlan = profile?.stripeSubscriptionPlan;

  if (!hasRecurring) {
    return {
      label: plan.checkoutLabel,
      action: 'checkout',
      optionId: plan.optionId,
      pendingLabel: 'Redirecting...',
    };
  }

  if (currentPlan === 'supporter' && plan.optionId === 'supporter') {
    return {
      label: plan.currentLabel,
      action: null,
    };
  }

  if (currentPlan === 'premium' && plan.optionId === 'supporter_plus') {
    return {
      label: plan.currentLabel,
      action: null,
    };
  }

  if (currentPlan === 'supporter' && plan.optionId === 'supporter_plus') {
    return {
      label: 'Change to Supporter Plus',
      action: 'change',
      optionId: 'supporter_plus',
      pendingLabel: 'Changing...',
    };
  }

  if (currentPlan === 'premium' && plan.optionId === 'supporter') {
    if (scheduledChange?.optionId === 'supporter') {
      return {
        label: 'Change scheduled',
        action: null,
      };
    }

    return {
      label: 'Change to Supporter',
      action: 'change',
      optionId: 'supporter',
      pendingLabel: 'Scheduling change...',
    };
  }

  return {
    label: plan.checkoutLabel,
    action: null,
  };
};

const SupportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [actionInfo, setActionInfo] = useState('');
  const [checkoutSyncInfo, setCheckoutSyncInfo] = useState('');
  const [pendingAction, setPendingAction] = useState('');
  const [scheduledChange, setScheduledChange] = useState(null);
  const [subscriptionState, setSubscriptionState] = useState(null);
  const [checkoutSyncRun, setCheckoutSyncRun] = useState(0);

  const loadProfile = useCallback(async () => {
    const user = await getUserProfile();

    setProfile(user);
    setScheduledChange(user.scheduledSubscriptionChange || null);
  }, []);

  useEffect(() => {
    let active = true;

    setLoading(true);
    loadProfile()
      .catch((error) => {
        if (!active) return;

        if (error?.response?.status === 401) {
          navigate('/login', { replace: true });
          return;
        }

        setLoadError('Unable to load support options. Please try again later.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [loadProfile, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentState = params.get('payment');

    if (paymentState !== 'success' && paymentState !== 'cancel') return;

    setActionError('');
    setCheckoutSyncInfo('');

    if (paymentState === 'success') {
      setActionInfo('');
      setActionSuccess(CHECKOUT_SUCCESS_MESSAGE);
      setCheckoutSyncRun((run) => run + 1);
    } else {
      setActionSuccess('');
      setActionInfo(CHECKOUT_CANCEL_MESSAGE);
    }

    params.delete('payment');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
        hash: location.hash,
      },
      { replace: true }
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (checkoutSyncRun === 0) return undefined;

    let active = true;
    const timeoutIds = CHECKOUT_SYNC_RETRY_DELAYS_MS.map((delay, index) =>
      window.setTimeout(async () => {
        try {
          await loadProfile();
        } catch {
          if (active && index === CHECKOUT_SYNC_RETRY_DELAYS_MS.length - 1) {
            setCheckoutSyncInfo('Your support status may take a moment to update.');
          }
        }
      }, delay)
    );

    return () => {
      active = false;
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [checkoutSyncRun, loadProfile]);

  const displayProfile = getProfileWithSubscriptionState(profile, subscriptionState);
  const hasRecurring = isActiveRecurring(displayProfile);
  const currentPeriodEnd = formatSupportDate(displayProfile?.stripeCurrentPeriodEnd);
  const scheduledEffectiveAt = formatSupportDate(scheduledChange?.effectiveAt);

  const refreshProfile = async () => {
    await loadProfile();
  };

  const handleCheckout = async (optionId, actionKey) => {
    setActionError('');
    setActionSuccess('');
    setActionInfo('');
    setCheckoutSyncInfo('');
    setPendingAction(actionKey);

    try {
      const result = await createCheckout(optionId);

      if (result.checkoutUrl) {
        window.location.assign(result.checkoutUrl);
      } else {
        throw new Error('Checkout could not be started.');
      }
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to start checkout.'));
      setPendingAction('');
    }
  };

  const handleChangeSubscription = async (optionId, actionKey) => {
    setActionError('');
    setActionSuccess('');
    setActionInfo('');
    setCheckoutSyncInfo('');
    setPendingAction(actionKey);

    try {
      const result = await changeSubscription(optionId);
      const effectiveAt = formatSupportDate(result.scheduledChange?.effectiveAt);

      setSubscriptionState(result.subscription || null);
      setScheduledChange(result.scheduledChange || null);

      if (result.scheduledChange?.optionId === 'supporter') {
        setActionSuccess(
          effectiveAt
            ? `Your change to Supporter is scheduled for ${effectiveAt}.`
            : 'Your change to Supporter is scheduled.'
        );
      } else if (optionId === 'supporter_plus') {
        setActionSuccess('Your monthly support has changed to Supporter Plus.');
      }

      await refreshProfile();
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to change subscription.'));
    } finally {
      setPendingAction('');
    }
  };

  const handleCancelSubscription = async () => {
    setActionError('');
    setActionSuccess('');
    setActionInfo('');
    setCheckoutSyncInfo('');
    setPendingAction('cancel-subscription');

    try {
      const result = await cancelSubscription();
      const periodEnd = formatSupportDate(result.subscription?.currentPeriodEnd);

      setSubscriptionState(result.subscription || null);
      setScheduledChange(null);
      setActionSuccess(
        periodEnd
          ? `Your subscription has been canceled. Your current support level remains active until ${periodEnd}.`
          : 'Your subscription has been canceled. Your current support level remains active until the end of the paid period.'
      );
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to cancel subscription.'));
    } finally {
      setPendingAction('');
    }
  };

  const handleResumeSubscription = async () => {
    setActionError('');
    setActionSuccess('');
    setActionInfo('');
    setCheckoutSyncInfo('');
    setPendingAction('resume-subscription');

    try {
      const result = await resumeSubscription();

      setSubscriptionState(result.subscription || null);
      setScheduledChange(null);
      setActionSuccess('Your subscription has been resumed.');
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to resume subscription.'));
    } finally {
      setPendingAction('');
    }
  };

  const handleMonthlyAction = (action) => {
    const actionKey = `monthly-${action.optionId}`;

    if (action.action === 'checkout') {
      handleCheckout(action.optionId, actionKey);
      return;
    }

    if (action.action === 'change') {
      handleChangeSubscription(action.optionId, actionKey);
    }
  };

  if (loading) {
    return (
      <main className="supportPage">
        <p>Loading support options...</p>
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="supportPage">
        <p className="supportError" role="alert">
          {loadError}
        </p>
      </main>
    );
  }

  return (
    <main className="supportPage">
      <section className="supportHero">
        <h1>Support ASiJoLab</h1>
        <p>
          ASiJoLab is currently free to use. If you find it useful, you can
          voluntarily support its continued development and help cover hosting
          and operating costs.
        </p>
        <p className="supportClarification">
          Supporting ASiJoLab does not currently unlock any additional features,
          tools or content. All currently available functionality remains
          available regardless of whether you support the project.
        </p>
      </section>

      {actionError && (
        <p className="supportError supportActionError" role="alert">
          {actionError}
        </p>
      )}
      {actionSuccess && (
        <p className="supportSuccess supportActionSuccess" role="status">
          {actionSuccess}
        </p>
      )}
      {actionInfo && (
        <p className="supportInfo supportActionInfo" role="status">
          {actionInfo}
        </p>
      )}
      {checkoutSyncInfo && (
        <p className="supportInfo supportActionInfo" role="status">
          {checkoutSyncInfo}
        </p>
      )}

      <section className="supportSection">
        <div className="supportSectionHeader">
          <h2>Monthly support</h2>
          <p>Regular support helps keep ASiJoLab available for the whole community.</p>
        </div>

        {hasRecurring && displayProfile.stripeCancelAtPeriodEnd && currentPeriodEnd && (
          <div className="subscriptionNotice">
            <p>Your subscription is scheduled to end on {currentPeriodEnd}.</p>
            <button
              type="button"
              onClick={handleResumeSubscription}
              disabled={Boolean(pendingAction)}
            >
              {pendingAction === 'resume-subscription' ? 'Resuming...' : 'Resume subscription'}
            </button>
          </div>
        )}

        {scheduledChange?.optionId === 'supporter' && scheduledEffectiveAt && (
          <div className="subscriptionNotice">
            <p>Changes to Supporter on {scheduledEffectiveAt}.</p>
          </div>
        )}

        <div className="monthlyGrid">
          {MONTHLY_PLANS.map((plan) => {
            const action = getMonthlyAction({ plan, profile: displayProfile, scheduledChange });
            const actionKey = `monthly-${plan.optionId}`;
            const isPending = pendingAction === actionKey;
            const isCurrent =
              (displayProfile.stripeSubscriptionPlan === 'supporter' &&
                plan.optionId === 'supporter') ||
              (displayProfile.stripeSubscriptionPlan === 'premium' &&
                plan.optionId === 'supporter_plus');

            return (
              <article
                className={`supportCard monthlyCard ${plan.emphasized ? 'supportCardEmphasis' : ''}`}
                key={plan.optionId}
              >
                {plan.label && <span className="supportCardLabel">{plan.label}</span>}
                <h3>{plan.title}</h3>
                <p className="supportPrice">{plan.price}</p>
                <p>{plan.description}</p>
                <ul>
                  {plan.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
                {isCurrent && !displayProfile.stripeCancelAtPeriodEnd && currentPeriodEnd && (
                  <p className="supportCardNote">Next payment: {currentPeriodEnd}</p>
                )}
                {isCurrent && displayProfile.stripeCancelAtPeriodEnd && currentPeriodEnd && (
                  <p className="supportCardNote">{plan.title} until {currentPeriodEnd}</p>
                )}
                <button
                  className="supportButton"
                  type="button"
                  onClick={() => handleMonthlyAction(action)}
                  disabled={!action.action || Boolean(pendingAction)}
                >
                  {isPending ? action.pendingLabel : action.label}
                </button>
              </article>
            );
          })}
        </div>

        {hasRecurring && !displayProfile.stripeCancelAtPeriodEnd && (
          <div className="subscriptionManageRow">
            <p>Your current monthly support continues until the end of the billing period after cancellation.</p>
            <button
              className="supportButton"
              type="button"
              onClick={handleCancelSubscription}
              disabled={Boolean(pendingAction)}
            >
              {pendingAction === 'cancel-subscription' ? 'Canceling...' : 'Cancel subscription'}
            </button>
          </div>
        )}
      </section>

      <section className="supportSection supportSectionSeparated">
        <div className="supportSectionHeader">
          <h2>One-time support</h2>
          <p>
            Make a voluntary one-time contribution to help cover operating costs
            and support continued development.
          </p>
        </div>

        <div className="oneTimeGrid">
          {ONE_TIME_OPTIONS.map((option) => {
            const actionKey = `checkout-${option.optionId}`;
            const isPending = pendingAction === actionKey;

            return (
              <article className="supportCard oneTimeCard" key={option.optionId}>
                <p className="oneTimePrice">{option.price}</p>
                <h3>{option.label}</h3>
                <p>{option.description}</p>
                <button
                  className="supportButton"
                  type="button"
                  onClick={() => handleCheckout(option.optionId, actionKey)}
                  disabled={Boolean(pendingAction)}
                >
                  {isPending ? 'Redirecting...' : option.buttonLabel}
                </button>
              </article>
            );
          })}
        </div>
      </section>

      <section className="supportInfoPanel">
        <h2>Good to know</h2>
        <ul>
          <li>All currently available ASiJoLab functionality is free to use.</li>
          <li>Support is voluntary and does not unlock additional features, tools or content.</li>
          <li>Monthly support can be changed or canceled at any time.</li>
          <li>One-time support does not pause or cancel your monthly subscription.</li>
        </ul>
      </section>
    </main>
  );
};

export default SupportPage;
